import React from 'react';
import { USER_INSIGHT_FIELD_TYPES } from '@/admin/api/insightFieldsApi';
import { USER_PERMISSION_FIELDS } from '../../UserDrawer/userFormValues';
import { getImportFieldLabel, getImportValueText } from './importFields';
import {
	USER_IMPORT_REVIEW_STATUSES,
	UserImportReviewStatusChip,
} from './UserImportReviewStatusChip';
import { ProblemTooltip } from './Fields/ProblemTooltip';
import { BrokerGroupsCell } from './Fields/BrokerGroupsCell';
import { InsightIdCell } from './Fields/InsightIdCell';
import { FlagCell } from './Fields/FlagCell';
import { PrimaryBrokerGroupEditCell } from './Fields/PrimaryBrokerGroupEditCell';
import { OtherBrokerGroupsEditCell } from './Fields/OtherBrokerGroupsEditCell';
import { InsightIdEditCell } from './Fields/InsightIdEditCell';
import { RoleEditCell } from './Fields/RoleEditCell';

// A cell with an error or warning is outlined in red or amber (styled by the grid's sx)
const getProblemClassName = ({ row, field }) =>
	(row.errors[field] && 'import-review-error') ||
	(row.warnings[field] && 'import-review-warning') ||
	'';

// Each field's column, with its problem shown on hover
const fieldColumn = ({ field, renderContent, ...column }) => ({
	field,
	headerName: getImportFieldLabel(field),
	sortable: false,
	editable: true,
	minWidth: 160,
	cellClassName: getProblemClassName,
	valueFormatter: (value) => getImportValueText(field, value),
	renderCell: ({ row, value }) => (
		<ProblemTooltip row={row} field={field}>
			{renderContent ? renderContent({ row, value }) : getImportValueText(field, value)}
		</ProblemTooltip>
	),
	...column,
});

/**
 * The user import review's columns: each row's status, then one column per import field.
 * Text fields edit in the cell on a double click; role, broker groups and Insight ids edit
 * with their dropdowns on one click (singleClickCell, with useCellModes); the
 * permissions are switches (onFlagChange(rowNumber, field, checked)), changed without
 * editing the cell.
 */
export const getUserImportReviewColumns = ({ onFlagChange }) => [
	{
		field: 'status',
		headerName: 'Status',
		width: 110,
		hideable: false,
		sortable: false,
		valueFormatter: (value) => USER_IMPORT_REVIEW_STATUSES[value].label,
		renderCell: ({ value }) => <UserImportReviewStatusChip status={value} />,
	},
	{ field: 'rowNumber', headerName: 'Row', width: 70, sortable: false },
	fieldColumn({ field: 'email', minWidth: 220 }),
	fieldColumn({ field: 'name', minWidth: 180 }),
	fieldColumn({
		field: 'brokerGroupId',
		minWidth: 220,
		singleClickCell: true,
		renderContent: ({ value }) => value && <BrokerGroupsCell ids={[value]} />,
		renderEditCell: (params) => <PrimaryBrokerGroupEditCell {...params} />,
	}),
	fieldColumn({
		field: 'brokerGroupArray',
		minWidth: 260,
		singleClickCell: true,
		renderContent: ({ value }) => <BrokerGroupsCell ids={value ?? []} />,
		renderEditCell: (params) => <OtherBrokerGroupsEditCell {...params} />,
	}),
	fieldColumn({ field: 'phoneNumber' }),
	fieldColumn({
		field: 'role',
		singleClickCell: true,
		renderEditCell: (params) => <RoleEditCell {...params} />,
	}),
	...Object.entries(USER_INSIGHT_FIELD_TYPES).map(([field, fieldType]) =>
		fieldColumn({
			field,
			minWidth: 200,
			singleClickCell: true,
			renderContent: ({ row, value }) => (
				<InsightIdCell brokerGroupId={row.brokerGroupId} fieldType={fieldType} value={value} />
			),
			renderEditCell: (params) => <InsightIdEditCell {...params} fieldType={fieldType} />,
		}),
	),
	...USER_PERMISSION_FIELDS.map((field) =>
		fieldColumn({
			field,
			minWidth: 150,
			editable: false,
			renderContent: ({ row, value }) => (
				<FlagCell
					value={value}
					onChange={(checked) => onFlagChange(row.rowNumber, field, checked)}
				/>
			),
		}),
	),
];
